import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import type { Consultation } from '@/types/patients';
import { usePatientConsultations } from './useConsultations';
import { formatVisitRecency } from './visitRecency';
import { formatShortDate } from './patientDisplay';
import { visitPreview } from './visitPreview';
import { SummaryCard, SummaryCardRow } from '@/components/synapse/SummaryCard';
import { features } from '@/lib/featureFlags';

interface VisitTimelineProps {
  patientId: string;
}

/**
 * Reverse-chronological list of the patient's consultations. Each row shows the
 * visit date (mono), a preview of the visit's diagnosis, and a draft/finalized
 * status badge.
 *
 * Rows are clickable and open the visit's read-only summary (`/visit/:id`).
 * With `features.doctorConsultEntry` on, a draft instead opens the editable
 * consultation form (`/consult/:id`) to continue history-taking.
 */
export function VisitTimeline({ patientId }: VisitTimelineProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = usePatientConsultations(patientId);

  if (isLoading) {
    return (
      <SummaryCard titleKey="patients.cardVisitTimeline" icon="list">
        {[0, 1, 2].map((i) => (
          <SummaryCardRow key={i} first={i === 0}>
            <div className="h-4 w-3/4 animate-pulse rounded bg-paper-3" />
          </SummaryCardRow>
        ))}
      </SummaryCard>
    );
  }

  if (isError) {
    const msg = error instanceof ApiError ? translateApiError(error) : t('patients.couldNotLoadVisits');
    return (
      <SummaryCard titleKey="patients.cardVisitTimeline" icon="list">
        <SummaryCardRow first>
          <span className="text-[12.5px] text-destructive">{msg}</span>
        </SummaryCardRow>
      </SummaryCard>
    );
  }

  const visits: Consultation[] = data ?? [];
  // Newest first. visitDate is a Date (parsed by the client adapter).
  const sorted = [...visits].sort((a, b) => b.visitDate.getTime() - a.visitDate.getTime());

  if (sorted.length === 0) {
    return (
      <SummaryCard titleKey="patients.cardVisitTimeline" icon="list">
        <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-paper-2 text-muted">
            <Icon name="list" size={18} />
          </div>
          <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('patients.noVisitsYet')}</div>
          <p className="max-w-[280px] text-[12px] text-muted">{t('patients.noVisitsBody')}</p>
        </div>
      </SummaryCard>
    );
  }

  return (
    <SummaryCard titleKey="patients.cardVisitTimeline" icon="list">
      {sorted.map((v, i) => {
        const preview = visitPreview(v.payload) ?? t('patients.visitNoDiagnosis');
        return (
          <SummaryCardRow key={v.id} first={i === 0}>
            <button
              type="button"
              onClick={() =>
                navigate(
                  v.status === 'draft' && features.doctorConsultEntry
                    ? `/patients/${patientId}/consult/${v.id}`
                    : `/patients/${patientId}/visit/${v.id}`,
                )
              }
              className="-mx-4 -my-3 flex w-[calc(100%+2rem)] items-center gap-3 px-4 py-3 text-left hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            >
              <span className="flex w-[72px] shrink-0 flex-col gap-0.5 text-muted">
                <span className="font-mono text-[12px] font-semibold leading-tight">
                  {formatShortDate(v.visitDate)}
                </span>
                <span className="text-[10.5px] leading-tight">{formatVisitRecency(v.visitDate, t)}</span>
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold tracking-[-0.01em]">
                {preview}
              </span>
              <Badge tone={v.status === 'finalized' ? 'success' : 'warn'} size="sm">
                {v.status === 'finalized' ? t('patients.statusFinalized') : t('patients.statusDraft')}
              </Badge>
            </button>
          </SummaryCardRow>
        );
      })}
    </SummaryCard>
  );
}
